/**
 * `Included -> Excluded`, on one line, for a control that shows neither value.
 *
 * A checkbox has no "new value" for a struck old one to sit under, so the
 * direction has to be written out. Use `StagedWas` where the control already
 * shows the new value.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { useDraft } from '../DraftContext';
import type { FieldPath } from '../types';
import { CS_PROPOSED_TEXT } from './stagedStyles';

export interface StagedTransitionProps {
  /** Every address this mark should describe. */
  readonly paths: readonly FieldPath[];
  /**
   * Where the mark starts, from the anchor's left edge. Defaults to the
   * checkbox's footprint, for rows whose anchor holds the checkbox before the
   * label. A row whose anchor is the label itself passes `0`.
   */
  readonly left?: string | number;
}

const StagedTransition = ({ paths, left = '36px' }: StagedTransitionProps) => {
  const { stagedFields } = useDraft();
  const changes = stagedFields.at(paths);

  if (changes.length === 0) {
    return null;
  }

  return (
    <>
      {changes.map((change) => (
        <Typography
          key={change.id}
          variant="caption"
          color="text.disabled"
          // The arrow is a glyph and is not read out; without this a screen
          // reader hears two bare words and loses the direction.
          aria-label={`${change.fromLabel} changing to ${change.toLabel}`}
          // Out of flow, so staging something does not shift the checkbox.
          // The call site owns the `position: relative` this hangs from.
          sx={{
            position: 'absolute',
            top: 'calc(100% + 2px)',
            // The checkbox's footprint by default, so this starts under the
            // label. Measured, and identical on every row including
            // sub-coverages.
            left,
            display: 'block',
            // A cap, not a preference: the shortest rows leave 12px below
            // the label and this cannot push a row open. Root font is 14px, so
            // 0.8rem is 11.2px. Larger clips.
            lineHeight: 1,
            whiteSpace: 'nowrap',
            fontSize: '0.8rem',
            pointerEvents: 'none',
          }}
        >
          {/* Not `stagedWasSx` — that is `display: block`. */}
          <Box component="span" sx={{ textDecoration: 'line-through' }}>
            {change.fromLabel}
          </Box>{' '}
          <Box component="span" aria-hidden>
            &rarr;
          </Box>{' '}
          <Box
            component="span"
            sx={{
              color: CS_PROPOSED_TEXT,
              fontWeight: 600,
            }}
          >
            {change.toLabel}
          </Box>
        </Typography>
      ))}
    </>
  );
};

export default StagedTransition;
